import { Offcanvas } from '../../global/offcanvas';

const SELECTORS = {
    closeButton: '[data-dismiss="offcanvasFilter"]',
    dropdownToggle: '.filter-dropdown-toggle',
    dropdown: '.filter-dropdown',
    dropdownLabel: '.filter-dropdown-label',
    dropdownItem: 'li',
    resetButton: '.reset-filter',
    submitButton: '.submit-filter',
    placeholder: '.is-placeholder',
};

const CLASSES = {
    open: 'open',
    hidden: 'hidden',
    selected: 'is-selected',
};

const AUTHOR_CONTENT_SELECTOR = '.flex.items-center';
const AUTHOR_FILTER_NAME = 'blog_authors';

/**
 * Handles filter dropdown functionality
 */
export class FilterDropdowns {
    constructor(offcanvas, options = {}) {
        this.offcanvas = offcanvas;
        this.options = {
            baseUrl: '/blog/archive/',
            ...options,
        };

        this.state = new Map();
        this.boundHandleClickOutside = this.handleClickOutside.bind(this);

        this.init();
    }

    init() {
        if (!this.offcanvas) {
            console.warn('Offcanvas element not provided');

            return;
        }

        this.setupCloseButton();
        this.cacheElements();

        if (this.submitButton && this.submitButton.dataset.baseUrl) {
            this.options.baseUrl = this.submitButton.dataset.baseUrl;
        }

        if (!this.dropdowns.length || !this.toggles.length) {
            console.warn('Dropdowns or toggles not found', {
                dropdowns: this.dropdowns.length,
                toggles: this.toggles.length,
            });

            return;
        }

        this.setupEventListeners();
        this.initializePlaceholders();

        // Add this line - initialize from URL after everything else is set up
        this.initializeFromURL();
    }

    /**
     * Initialize dropdowns from URL parameters
     */
    initializeFromURL() {
        const urlParams = new URLSearchParams(window.location.search);

        this.dropdowns.forEach((dropdown) => {
            const filterName = dropdown.dataset.name;
            const paramValue = urlParams.get(`_${filterName}`);

            if (paramValue) {
                // Find the matching item
                const item = dropdown.querySelector(`${SELECTORS.dropdownItem}[data-value="${CSS.escape(paramValue)}"]`);
                const toggle = this.offcanvas.querySelector(`[data-target="${dropdown.id}"]`);

                if (item && toggle) {
                    // Update state
                    this.state.set(filterName, paramValue);

                    // Update selected state
                    this.updateSelectedState(dropdown, item);

                    // Update label (handle author avatars)
                    const authorContent = item.querySelector(AUTHOR_CONTENT_SELECTOR);

                    if (authorContent && filterName === AUTHOR_FILTER_NAME) {
                        const labelContent = authorContent.cloneNode(true);
                        labelContent.classList.remove('pointer-events-none');
                        this.updateLabel(toggle, labelContent, true);
                    } else {
                        const text = item.textContent.trim();
                        this.updateLabel(toggle, text);
                    }
                }
            }
        });
    }

    /**
     * Setup offcanvas close button
     */
    setupCloseButton() {
        const closeButton = this.offcanvas.querySelector(SELECTORS.closeButton);

        if (closeButton) {
            closeButton.addEventListener('click', () => {
                const offcanvasInstance = Offcanvas.getInstance(this.offcanvas);

                if (offcanvasInstance) {
                    offcanvasInstance.hide();
                }
            });
        }
    }

    /**
     * Cache DOM elements
     */
    cacheElements() {
        this.dropdowns = Array.from(this.offcanvas.querySelectorAll(SELECTORS.dropdown));
        this.toggles = Array.from(this.offcanvas.querySelectorAll(SELECTORS.dropdownToggle));
        this.resetButton = this.offcanvas.querySelector(SELECTORS.resetButton);
        this.submitButton = this.offcanvas.querySelector(SELECTORS.submitButton);
    }

    /**
     * Setup all event listeners
     */
    setupEventListeners() {
        this.setupToggleListeners();
        this.setupDropdownItemListeners();
        this.setupResetButton();
        this.setupSubmitButton();

        // Add global click listener for closing dropdowns
        document.addEventListener('click', this.boundHandleClickOutside);
    }

    /**
     * Setup toggle button listeners
     */
    setupToggleListeners() {
        this.toggles.forEach((toggle) => {
            const targetId = toggle.dataset.target;
            const dropdown = this.offcanvas.querySelector(`#${targetId}`);

            if (!dropdown) {
                console.warn('Dropdown not found for toggle:', targetId);

                return;
            }

            toggle.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                this.toggleDropdown(toggle, dropdown);
            });
        });
    }

    /**
     * Setup dropdown item listeners
     */
    setupDropdownItemListeners() {
        this.dropdowns.forEach((dropdown) => {
            const toggle = this.offcanvas.querySelector(`[data-target="${dropdown.id}"]`);
            const items = dropdown.querySelectorAll(SELECTORS.dropdownItem);

            items.forEach((item) => {
                item.addEventListener('click', (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    this.handleItemClick(item, dropdown, toggle);
                });
            });
        });
    }

    /**
     * Setup reset button
     */
    setupResetButton() {
        if (this.resetButton) {
            this.resetButton.addEventListener('click', (e) => {
                e.preventDefault();
                this.resetFilters();
            });
        }
    }

    /**
     * Setup submit button
     */
    setupSubmitButton() {
        if (this.submitButton) {
            this.submitButton.addEventListener('click', (e) => {
                e.preventDefault();
                this.submitFilters();
            });
        }
    }

    /**
     * Initialize placeholder selections
     */
    initializePlaceholders() {
        this.dropdowns.forEach((dropdown) => {
            const placeholder = dropdown.querySelector(SELECTORS.placeholder);

            if (placeholder) {
                placeholder.classList.add(CLASSES.selected);
            }
        });
    }

    /**
     * Toggle dropdown visibility
     */
    toggleDropdown(toggle, dropdown) {
        const isOpen = dropdown.classList.contains(CLASSES.open);

        // Close all dropdowns first
        this.closeAllDropdowns();

        // Toggle current dropdown
        if (!isOpen) {
            dropdown.classList.add(CLASSES.open);
            dropdown.classList.remove(CLASSES.hidden);
            this.rotateToggleIcon(toggle, true);
        }
    }

    /**
     * Close all dropdowns
     */
    closeAllDropdowns() {
        this.dropdowns.forEach((dropdown) => {
            dropdown.classList.remove(CLASSES.open);
            dropdown.classList.add(CLASSES.hidden);
        });

        this.toggles.forEach((toggle) => {
            this.rotateToggleIcon(toggle, false);
        });
    }

    /**
     * Rotate toggle icon
     */
    rotateToggleIcon(toggle, open) {
        toggle.classList.toggle('is-open', open);
    }

    /**
     * Update dropdown label with text or HTML
     */
    updateLabel(toggle, content, isHTML = false) {
        const label = toggle.querySelector(SELECTORS.dropdownLabel);

        if (!label) {
            return;
        }

        if (isHTML) {
            // Sanitize by creating elements through DOM API
            const temp = document.createElement('div');
            temp.appendChild(content);
            label.textContent = '';
            label.appendChild(content);
        } else {
            label.textContent = content;
        }
    }

    /**
     * Handle dropdown item selection
     */
    handleItemClick(item, dropdown, toggle) {
        const value = item.dataset.value || '';
        const filterName = dropdown.dataset.name;

        // Update state
        this.state.set(filterName, value);

        // Check if this is an author with avatar content
        const authorContent = item.querySelector(AUTHOR_CONTENT_SELECTOR);

        if (authorContent && filterName === AUTHOR_FILTER_NAME) {
            const labelContent = authorContent.cloneNode(true);
            labelContent.classList.remove('pointer-events-none');
            this.updateLabel(toggle, labelContent, true);
        } else {
            const text = item.textContent.trim();
            this.updateLabel(toggle, text);
        }

        // Update selected state
        this.updateSelectedState(dropdown, item);

        // Close dropdown
        dropdown.classList.remove(CLASSES.open);
        dropdown.classList.add(CLASSES.hidden);
        this.rotateToggleIcon(toggle, false);
    }

    /**
     * Update selected state for dropdown items
     */
    updateSelectedState(dropdown, selectedItem) {
        dropdown.querySelectorAll(SELECTORS.dropdownItem).forEach((item) => {
            item.classList.remove(CLASSES.selected);
        });
        selectedItem.classList.add(CLASSES.selected);
    }

    /**
     * Reset all filters to default state
     */
    resetFilters() {
        this.state.clear();

        this.dropdowns.forEach((dropdown) => {
            const toggle = this.offcanvas.querySelector(`[data-target="${dropdown.id}"]`);
            const placeholder = dropdown.querySelector(SELECTORS.placeholder);

            // Remove selected state from all items
            dropdown.querySelectorAll(SELECTORS.dropdownItem).forEach((item) => {
                item.classList.remove(CLASSES.selected);
            });

            // Add selected to placeholder and update label
            if (placeholder && toggle) {
                placeholder.classList.add(CLASSES.selected);
                this.updateLabel(toggle, placeholder.textContent.trim());
            }
        });
    }

    /**
     * Submit filters and navigate
     */
    submitFilters() {
        const params = new URLSearchParams();

        // Add all active filters to params
        this.state.forEach((value, key) => {
            if (value) {
                params.append(`_${key}`, value);
            }
        });

        // Build URL
        const queryString = params.toString();
        const finalUrl = queryString ? `${this.options.baseUrl}?${queryString}#entries` : `${this.options.baseUrl}#entries`;

        // Navigate to URL
        window.location.href = finalUrl;
    }

    /**
     * Handle clicks outside dropdowns
     */
    handleClickOutside(e) {
        const isToggle = e.target.closest(SELECTORS.dropdownToggle);
        const isDropdown = e.target.closest(SELECTORS.dropdown);

        if (!isToggle && !isDropdown) {
            this.closeAllDropdowns();
        }
    }

    /**
     * Clean up event listeners
     */
    destroy() {
        document.removeEventListener('click', this.boundHandleClickOutside);
    }
}
